import { z } from "zod"
import { Provider } from "./channels.js"
import { CommentMedia, CommentSummary, ControlMode, ConversationStatus } from "./conversations.js"
import { Id } from "./ids.js"
import { AssessmentCategory, RiskLevel } from "./moderation.js"

// ---- Work-item index: one inbox over private messages and public comments ----

export const WorkItemKind = z.enum(["conversation", "comment"])
export type WorkItemKind = z.infer<typeof WorkItemKind>
/** Views are queries over explicit fields, never a single status enum. */
export const WorkView = z.enum(["attention", "mine", "unassigned", "waiting", "done", "automation", "all"])
export type WorkView = z.infer<typeof WorkView>
export const WorkType = z.enum(["all", "messages", "comments"])
export type WorkType = z.infer<typeof WorkType>
export const WorkSort = z.enum(["oldest_unanswered", "newest_activity"])
export type WorkSort = z.infer<typeof WorkSort>
/** The mark shown for the source: Instagram, a Facebook Page (comments) or Messenger (private messages). */
export const ChannelMark = z.enum(["instagram", "facebook", "messenger"])
export type ChannelMark = z.infer<typeof ChannelMark>
/** The single most relevant cue for a row; everything else lives in the detail pane. */
export const WorkCue = z.enum(["needs_person", "automation", "needs_reply", "replied", "waiting", "done", "hidden", "deleted"])
export type WorkCue = z.infer<typeof WorkCue>
export const CommentVisibility = z.enum(["visible", "hidden", "deleted"])
export type CommentVisibility = z.infer<typeof CommentVisibility>

export const WorkItem = z.object({
  kind: WorkItemKind,
  id: Id,
  contactId: Id,
  contactName: z.string(),
  handle: z.string().nullable(),
  provider: Provider,
  channel: ChannelMark,
  connectionId: Id,
  accountName: z.string(),
  accountHandle: z.string().nullable(),
  /** Profile picture when the provider has released it (App Review); null falls back to initials. */
  avatarUrl: z.string().nullable(),
  /** Up to three contact tags, for the row chip. */
  tags: z.array(z.string()),
  preview: z.string().nullable(),
  unread: z.boolean(),
  lastActivityAt: z.iso.datetime(),
  /** Since when the customer has been waiting for a reply; null once answered. Internal notes never clear it. */
  waitingSince: z.iso.datetime().nullable(),
  assigneeMembershipId: Id.nullable(),
  assigneeName: z.string().nullable(),
  cue: WorkCue.nullable(),
  conversation: z.object({ status: ConversationStatus, controlMode: ControlMode, handoverRequestedAt: z.iso.datetime().nullable() }).nullable(),
  comment: z
    .object({
      status: z.enum(["new", "handled", "hidden", "deleted"]),
      visibility: CommentVisibility,
      isAd: z.boolean(),
      adTitle: z.string().nullable(),
      mediaExternalId: z.string().nullable(),
      media: CommentMedia.nullable(),
      /** Strongest flagged category from the latest assessment, for the row chip. */
      risk: z.object({ category: AssessmentCategory, level: RiskLevel }).nullable(),
    })
    .nullable(),
})
export type WorkItem = z.infer<typeof WorkItem>

export const WorkItemFilters = z.object({
  view: WorkView.optional(),
  type: WorkType.optional(),
  /** Comma-separated connection ids. */
  accounts: z.string().max(2000).optional(),
  /** A membership id, "me" or "none". Comments have no assignee yet, so "none" keeps them and any other value drops them. */
  assignee: z.string().max(60).optional(),
  origin: z.enum(["organic", "ad"]).optional(),
  /** Default "visible": hidden and deleted comments only appear when asked for. */
  visibility: z.enum(["visible", "hidden", "deleted", "any"]).optional(),
  /** External media id of a post: "other comments on this post". */
  post: z.string().max(200).optional(),
  /** "true" keeps only unread conversations; comments carry no read state and drop out. */
  unread: z.enum(["true"]).optional(),
  /** Comments the classifier flagged with this category; conversations drop out. */
  category: AssessmentCategory.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  q: z.string().trim().max(120).optional(),
})
export type WorkItemFilters = z.infer<typeof WorkItemFilters>

export const WorkItemsQuery = WorkItemFilters.extend({
  sort: WorkSort.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().max(400).optional(),
})
export type WorkItemsQuery = z.infer<typeof WorkItemsQuery>

export const WorkItemsPage = z.object({
  items: z.array(WorkItem),
  /** Opaque; pass back as `cursor` for the next page. Null when this page is the last. */
  nextCursor: z.string().nullable(),
  /** Every item in the selected scope, not only the fetched page. */
  total: z.number().int(),
  breakdown: z.object({ conversations: z.number().int(), comments: z.number().int() }),
  /** Per-view totals under the same filters, for the view selector. */
  counts: z.object({ attention: z.number().int(), mine: z.number().int(), unassigned: z.number().int(), waiting: z.number().int(), done: z.number().int(), automation: z.number().int(), all: z.number().int() }),
  /** Unread items inside the current view and filters. */
  unread: z.number().int(),
  sort: WorkSort,
})
export type WorkItemsPage = z.infer<typeof WorkItemsPage>

// ---- Personal saved views ----

export const InboxView = z.object({ id: Id, name: z.string(), filters: WorkItemFilters, sort: WorkSort.nullable(), createdAt: z.iso.datetime(), updatedAt: z.iso.datetime() })
export type InboxView = z.infer<typeof InboxView>
export const SaveInboxViewInput = z.object({ name: z.string().trim().min(1).max(60), filters: WorkItemFilters, sort: WorkSort.nullable().default(null) })
export type SaveInboxViewInput = z.infer<typeof SaveInboxViewInput>

// ---- A comment with the customer follow-ups threaded under it ----

export const CommentThread = z.object({ comment: CommentSummary, followUps: z.array(CommentSummary) })
export type CommentThread = z.infer<typeof CommentThread>
